import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import { onError } from 'utils/handlers';
import { DATE_FRONTEND_FORMAT, dateFormatterToReporteador } from 'utils/formatValues';
import { FilePdfOutlined } from '@ant-design/icons';
import Previewer from 'components/Previewer';
import { postPolizaDeIngresos } from 'api/recaudacion';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import Select from 'components/Select';
import { toMoment } from 'utils/normalizers';

export const permissionConfig = {
  mainPermission: 'reportes_contabilidad',
  required: ['view_unidadderecaudacion'],
};

const posts = {
  1: postPolizaDeIngresos,
};

const ReportesDeContabilidad = () => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visiblePreviewer, setVisiblePreviewer] = useState(false);
  const [base64, setBase64] = useState('');
  const [unidadesRecaudadoras, setUnidadesRecaudadoras] = useState([]);

  const data = [
    {
      id: 1,
      nombre: 'Póliza de Ingresos',
    },
  ];

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _unidadesRecaudadoras = await getUnidadesDeRecaudacion();
      if (mounted) {
        setUnidadesRecaudadoras(_unidadesRecaudadoras);
        setLoading(false);
      }
    };

    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setBase64('');
  };

  const formatValues = (id, values) => {
    const formatters = {
      1: () => {
        const _values = { ...values };
        _values.fecha_inicial = dateFormatterToReporteador(_values.fechas[0], true, '-');
        _values.fecha_final = dateFormatterToReporteador(_values.fechas[1], false, '-');
        delete _values.fechas;
        return _values;
      },
    };

    return formatters[id](values);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const _values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const values = formatValues(key, _values);
      await posts[key](values);
      onCancel();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickConsultar = () => {
    form.resetFields();
    setVisible(true);
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Nombre del reporte',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 350,
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          customActions={[{
            icon: <FilePdfOutlined />,
            text: 'Consultar',
            onClick: onClickConsultar,
          }]}
          rowKey="id"
        />
        <Modal
          visible={visible}
          footer={null}
          closable={false}
          maskClosable={false}
          title={(
            <FormSubmitControls
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              label="Consulta de Reporte"
              onFinishText="Ejecutar"
            />
          )}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
          >
            <Row gutter={10}>
              <Col span={24}>
                <Form.Item
                  label="Unidad de Recaudación"
                  rules={rules.required}
                  name="unidad_recaudadora_id"
                >
                  <Select dataSource={unidadesRecaudadoras} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Fecha Inicial y Final"
                  rules={rules.required}
                  name="fechas"
                >
                  <DatePicker.RangePicker
                    placeholder=""
                    format={DATE_FRONTEND_FORMAT}
                    disabledDate={(current) => current && current > toMoment(new Date()).endOf('day')}
                  />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
      </Spin>
      <Previewer
        visible={visiblePreviewer}
        base64={base64}
        setVisible={(value) => {
          setVisiblePreviewer(value);
          onCancel();
        }}
        allowSend
        isImage={false}
        allowDownload
        fileName={data.find((item) => item.id === selectedRowKeys[0])?.nombre}
      />
    </Row>
  );
};

export default ReportesDeContabilidad;
